import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Icon, ButtonIcon, TextInput, Switcher, Button, Checkbox, FileUploadV2, Modal, Notification, DataTable, Breadcrumb, Search, Radio, TextArea, Dropdown } from "pepsico-ds";
import { useCallback, useEffect, useRef, useState } from "react";
import "./app-retirement.css"
import appRetirementService from "./app-retirement.service"; 
import { toast, ToastContainer } from 'react-toastify';
import { useLoading } from "../../loader";

const AppRetirement = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { REACT_APP_API_ENDPOINT } = process.env;
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const { showLoader, hideLoader } = useLoading();
    const [showModal, setShowModal] = useState(false);
    const [copyButtonText, setCopyButtonText] = useState(t('nonArExtension.copy'));
    const [requestData, setRequestData] = useState(null);
    const [disableAppSearch, setDisableAppSearch] = useState(false);
    const [applications, setApplications] = useState([]);
    const [selectedApp, setSelectedApp] = useState({});
    const [selectedAppDetails, setSelectedAppDetails] = useState({});
    const [searchedAppQuery, setSearchedAppQuery] = useState("");
    const [businessJustification, setBusinessJustification] = useState("");
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [certificateToBeRevokedIndex, setCertificateToBeRevokedIndex] = useState(0);
    const [disableCertificateCommonName, setDisableCertificateCommonName] = useState(false);
    const [certificateCommonName, setCertificateCommonName] = useState("");
    const [serialNumberOptions, setSerialNumberOptions] = useState([]);
    const [selectedSerialNumbers, setSelectedSerialNumbers] = useState([]);

    const onCertificateCommonNameSearch = async() => {
        try {
            showLoader();
            const payload = {
                commonName: certificateCommonName
            }
            setDisableCertificateCommonName(true);
            const response = await appRetirementService.validateCertificate(payload);
            setDisableCertificateCommonName(false);
            if (response?.valid) {
                toast.success(response?.message);
                const versionResponse = await appRetirementService.certificateVersions(payload);
                if(versionResponse?.certificateVersions){
                    const serialNumbers = versionResponse?.certificateVersions?.map((d)=>({ displayText: d.serialNumber, id: d.serialNumber ,...d}))
                    serialNumbers.length && setSerialNumberOptions(serialNumbers);
                }else{
                    toast.error(versionResponse?.message || versionResponse?.errorMessage);
                }

            }else{
                toast.error(response?.message || response?.errorMessage);
            }
            hideLoader();
        } catch (error) {
            hideLoader();
            toast.error(error.message, "error");
            resetFields();
        } finally {
            hideLoader();
        }
    }

    const searchApplications = async (text = searchedAppQuery) => {
        if (!text || text?.trimStart()?.length < 5) {
            toast.error(t("app_retirement.please_enter_at_least_5_characters_to_search_for_an_application"));
            return;
        }
        setApplications([]);
        try {
            showLoader();
            setDisableAppSearch(true);
            const payload = {
                searchText: text
            }
            const response = await appRetirementService.searchApplications(payload);
            if (response?.status === "Failed" || response?.errorMessage) {
                toast.error(response?.errorMessage);
            }else{
                if(response?.length){
                    const mapped = response.map((item) => ({
                        id: item?.appId,
                        label: item?.applicationName,
                    }));
                    setApplications(mapped);
                }else{
                    toast.error(t("app_retirement.no_application_available"));
                }
            }
            hideLoader();
            setDisableAppSearch(false);
        } catch (error) {
            toast.error(error.message)
            hideLoader();
            setDisableAppSearch(false);
        } finally {
            hideLoader();
            setDisableAppSearch(false);
        }
    };

    // function useDebouncedCallback(callback, delay = 1000) {
    //     const timerRef = useRef();

    //     const debounced = useCallback((...args) => {
    //         if (timerRef.current) {
    //             clearTimeout(timerRef.current);
    //         }
    //         timerRef.current = setTimeout(() => {
    //             callback(...args);
    //         }, delay);
    //     }, [callback, delay]);

    //     useEffect(() => {
    //         return () => {
    //             if (timerRef.current) {
    //                 clearTimeout(timerRef.current);
    //             }
    //         };
    //     }, []);

    //     return debounced;
    // }

    // const debouncedSearch = useDebouncedCallback((q) => {
    //     if (q?.trimStart()?.length >= 5) {
    //         searchApplications(q);
    //     } else {
    //         setApplications([]);
    //     }
    // }, 500);

    useEffect(()=>{
        (async()=>{
            if(selectedApp?.id){
                try {
                    showLoader();
                    const payload = {
                        appId: selectedApp?.id?.includes?.("-") ? selectedApp?.id?.split("-")?.[1] : selectedApp?.id
                    }
                    const response = await appRetirementService.getApplicationDetails(payload);
                    if (response?.status === "Failed" || response?.errorMessage) {
                        toast.error(response?.errorMessage);
                    }else{
                        const appDetails = {
                            applicationOwner: response.applicationOwner,
                            activeUsers: response.activeUsers,
                            typeOfIntegration: response.typeOfIntegration
                        }
                        setSelectedAppDetails(appDetails);
                    }
                    hideLoader();
                } catch (error) {
                    hideLoader();
                    toast.error(error.message, "error");
                    resetFields();
                } finally {
                    hideLoader();
                }
            }
        })()
    },[selectedApp])

    const resetFields = () => {
    }

    const handleSubmit = async () => {
        try {
            showLoader();
            const payload = {
                appId: selectedApp?.id?.includes?.("-") ? selectedApp?.id?.split("-")?.[1] : selectedApp?.id,
                businessJustification: businessJustification, 
                acknowledgementAccepted: isConfirmed, 
                certificateToRevoke: selectedSerialNumbers.length>0 ? true : false,
                certificateDetails: selectedSerialNumbers?.map((a)=> ({commonName: a?.commonName, serialNumber: a?.serialNumber, validToDate: a?.validToDate})) 
            }
            const response = await appRetirementService.submitRetirementRequest(payload);
            if (response?.requestNumber ) {
                setRequestData(response)
                setShowModal(true);
            }else{
                toast.error(response?.errorMessage);
            }
            hideLoader();
        } catch (error) {
            hideLoader();
            toast.error(t("entraGuest.request_fail_message"), "error");
            resetFields();
        } finally {
            hideLoader();
        }
    }

    const disableSubmit = () => {
        return !isConfirmed || !selectedApp?.id  || !businessJustification || (certificateToBeRevokedIndex===0 && !selectedSerialNumbers?.length)
    }

    const closeModal = () => {
        setShowModal(false);
    }

    const onClickCopynumber = () => {
        if (requestData?.requestNumber) {
            navigator.clipboard.writeText(requestData?.requestNumber?.toString?.()).then(() => {
                setCopyButtonText(t('nonArExtension.copied'));
            }).catch(err => {
                console.error("Failed to copy text: ", err);
            });
        }
    };

    const onViewRequest = () => {
        const statePayload = { type: "requestNumber", value: requestData?.requestNumber };
        navigate("/audit-requests", { state: statePayload });
    }

    const renderConfirmModal = () => {
        return (
            <div className="confirm-modal">
                <Modal
                    showModal={showModal}
                    className="entra-request-success-modal"
                    onCloseModal={closeModal}
                    closeOnOutsideClick={false}
                    notificationsProps={{
                        secondaryActionLabel: (
                            <>
                                <Icon
                                    alt="alt value"
                                    icon={copyButtonText == t('nonArExtension.copied') ? "check" : "content_copy"}
                                    size="medium"
                                    style={{ marginRight: '6px', display: 'unset' }}
                                />

                                {copyButtonText}
                            </>
                        ),
                        secondaryActionOnClick: onClickCopynumber,
                        text: `${requestData?.requestNumber}`,
                        title: t('nonArExtension.request_number_title'),
                        type: 'info'
                    }}
                    showAuxiliaryButton={false}
                    showLink={false}
                    showPrimaryButton={true}
                    primaryButtonProps={{
                        onClick: onViewRequest,
                        size: 'medium',
                        text: t('homePage.view_request'),
                        variant: 'primary'
                    }}
                    showSecondaryButton={false}
                    showSlot
                    showTertiaryButton={true}
                    tertiaryButtonProps={{
                        onClick: closeModal,
                        size: 'medium',
                        text: t('nonArExtension.back'),
                        variant: 'tertiary'
                    }}
                    showCloseButton={true}
                    style={{}}
                    text={<>
                        <div style={{ marginTop: '10px' }}>
                            <p className="modal-para-text">{`${t('app_retirement.application_retirement_submitted_successfully_sub_heading')} ${selectedApp?.label}`}
                            </p>
                        </div>
                    </>}
                    title={t('app_retirement.application_retirement_submitted_successfully')}
                    type="fluid"
                    viewPort={`${isMobile ? "mobile" : "desktop"}`}
                >
                    <p style={{ fontSize: '16px' }} className="modal-para-text">{`${t('entraGuest.see_all_your_requests_in_the')} `} <span className="view-request-txt">{`${t('entraGuest.view_request')} `}</span> {t('entraGuest.section_below')}
                    </p>
                </Modal>
            </div>
        )
    }

    return (
        <>
            <div className="app-retirement-container entra-guest-main-container">
                <div
                    className={isMobile ? "row" : "container"}
                    style={{
                        background: "none",
                        boxShadow: "none",
                        justifyContent: isMobile ? "center" : "",
                    }}
                >
                    <div
                        style={{
                            width: "100%",
                            flexDirection: isMobile ? "column" : "row",
                            gap: isMobile ? "0px" : "15px",
                            flexWrap: "wrap",
                            flex: 1,
                        }}
                    >
                        <div className={isMobile ? "row mt-2" : ""} style={{ justifyContent: isMobile ? "center" : "" }}>
                            <div
                                className={`${isMobile ? 'pl-4 pr-4 ml-4 mr-4' : ''} ad-main-heading-container`}
                            >
                                <Breadcrumb
                                    homeIcon={{
                                        icon: "home",
                                        onClick: () => {
                                            navigate("/home")
                                        }
                                    }}
                                    breadcrumbs={[
                                        {
                                            label: "Home",
                                        },
                                        {
                                            label: t("app_retirement.application_retirements"),
                                        },
                                    ]}
                                    className="entra-breadcrumb"
                                    useAnchor
                                />

                                <div className="ad-page-header">
                                    <div className="ad-main-heading">
                                        {t("app_retirement.application_retirements")}
                                    </div>

                                    <div className="ad-main-desc">
                                        {t("app_retirement.raise_a_request_to_application_retirement")}
                                    </div>
                                </div>
                            </div>
                            <div
                                className="card-layout reg-card mb-3 mb-md-0 active-directory-card-container entra-guest-container"
                                style={{
                                    flex: isMobile ? "unset" : "0 0 calc(66.666% - 7.5px)",
                                    width: isMobile ? "100%" : "auto",
                                    flexDirection: isMobile ? "column" : "row",
                                    gap: "1rem"
                                }}
                            >
                                <div style={{ ...(!isMobile && { flex: "2" }) }}>
                                    <div
                                        className="card-layout reg-card mb-3 mb-md-0 active-directory-card-container idx-container"
                                        style={{
                                            flex: isMobile ? "unset" : "0 0 calc(66.666% - 7.5px)",
                                            width: isMobile ? "100%" : "auto",
                                            flexDirection: isMobile ? "column" : "row",
                                            gap: "1rem",
                                        }}
                                    >
                                        <div className="w-100">
                                            <div className="card card-content align-items-center">
                                                <div className={`bulk-upload-section ${isMobile ? "" : "w-75"}`}>
                                                    <div className="form-field mb-3">
                                                        <div className="" style={{ display: 'flex', justifyContent: "space-between" }}>
                                                            <label className="safe-name" style={{ display: "flex", alignItems: "center" }}>
                                                                {t("app_retirement.application_name")}
                                                            </label>
                                                            <label className="required-text $font-xsmall-regular" style={{ color: "var(--color-text-secondary)" }}>*Required</label>
                                                        </div>
                                                        <Search
                                                            className="create-safe-app-name"
                                                            isAutoComplete
                                                            isClearable={true}
                                                            required
                                                            isRequired
                                                            maxLength={20}
                                                            isDisabled={disableAppSearch}
                                                            onClear={() => {
                                                                setSelectedApp(null);
                                                            }}
                                                            onSelectOption={(val) => {
                                                                const sel = Array.isArray(val) ? val[0] : val;
                                                                setSelectedApp(sel);
                                                            }}
                                                            onSearch={(val) => searchApplications(val)}
                                                            onUpdate={(text) => {
                                                                // debouncedSearch(text);
                                                                setSearchedAppQuery(text.trimStart())
                                                            }}
                                                            placeholderText={t("app_retirement.application_name_placeholder")}
                                                            size="medium"
                                                            autoComplete="off"
                                                            suggestions={applications}
                                                            selectedValue={selectedApp}
                                                            limitSuggestions={applications.length}
                                                            searchTerm={searchedAppQuery}
                                                        />
                                                    </div>
                                                    {
                                                        (selectedAppDetails?.applicationOwner || selectedAppDetails?.applicationOwner || selectedAppDetails?.applicationOwner) ? 
                                                            <div className="application-details-section mb-3">
                                                                <div className="heading mb-2">{t("app_retirement.application_details")}</div>
                                                                <div className="owner-active-user-section mb-2">
                                                                    <div className="app-owner">
                                                                        <div className="heading">{t("app_retirement.application_owner")}</div>
                                                                        <div className="detail">{selectedAppDetails?.applicationOwner}</div>
                                                                    </div>
                                                                    <div className="active-user">
                                                                        <div className="heading">{t("app_retirement.active_user_associated")}</div>
                                                                        <div className="detail">{selectedAppDetails?.activeUsers}</div>
                                                                    </div>
                                                                </div>
                                                                <div className="integration-type-section">
                                                                    <div className="heading mb-2">{t("app_retirement.type_of_integration")}</div>
                                                                    <div className="content-section">
                                                                        {
                                                                            selectedAppDetails?.typeOfIntegration?.split(",").map((rec)=>{
                                                                                return <div className="tag">{rec?.trim()}</div>
                                                                            })
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        : null
                                                    }
                                                    <Radio
                                                        altText=""
                                                        className="mb-3"
                                                        feedbackType="info"
                                                        items={[
                                                            'Yes',
                                                            'No'
                                                        ]}
                                                        label={t("app_retirement.do_you_have_a_certificate_to_be_revoked")}
                                                        orientation="horizontal"
                                                        reference="radio button"
                                                        showText
                                                        size="small"
                                                        style={{}}
                                                        required={true}
                                                        selectedItemIndex={certificateToBeRevokedIndex}
                                                        onUpdate={(index, value) => {
                                                            setCertificateToBeRevokedIndex(index);
                                                        }}
                                                    />
                                                    {
                                                        certificateToBeRevokedIndex === 0 ?
                                                        <div className="certificate-details-section mb-3">
                                                            <div>
                                                                <div className="mb-2" style={{ display: 'flex', justifyContent: "space-between" }}>
                                                                    <label className="safe-name" style={{ display: "flex", alignItems: "center" }}>
                                                                        {t("app_retirement.certificate_common_name")}
                                                                    </label>
                                                                    <label className="required-text $font-xsmall-regular" style={{ color: "var(--color-text-secondary)" }}>*Required</label>
                                                                </div>
                                                                <Search
                                                                    value={certificateCommonName}
                                                                    isClearable={!!certificateCommonName.length}
                                                                    menuPosition="bottom-right"
                                                                    onSelectOption={function oFe() { }}
                                                                    onUpdate={(val)=> {
                                                                        setCertificateCommonName(val);
                                                                    }}
                                                                    size="medium"
                                                                    delay={0}
                                                                    className="create-safe-app-name"
                                                                    isDisabled={disableCertificateCommonName}
                                                                    onClear={() => {
                                                                        setCertificateCommonName("");
                                                                        setSerialNumberOptions([]);
                                                                        setSelectedSerialNumbers([]);
                                                                    }}
                                                                    onSearch={() => onCertificateCommonNameSearch()}
                                                                    placeholderText={t("app_retirement.enter_certificate_common_name")}
                                                                />
                                                            </div>
                                                            <Dropdown
                                                                childList={serialNumberOptions?.sort?.((a, b) => { return a?.displayText?.localeCompare?.(b.displayText); })}
                                                                label={t("app_retirement.serial_number_latest_version")}
                                                                disabled={!serialNumberOptions?.length}
                                                                placeholder="Select"
                                                                isClearable={false}
                                                                isRequired
                                                                selection="multiple"
                                                                size="medium"
                                                                setSelectedValue={(selected) => {
                                                                    setSelectedSerialNumbers(selected)
                                                                }}
                                                                selectedValue={selectedSerialNumbers}
                                                                onKeyDown={(e) => e.preventDefault()}
                                                                showLabelIcon={false}
                                                            />
                                                        </div>
                                                        : null
                                                    }
                                                    <TextArea
                                                        title={t("app_retirement.business_justification")}
                                                        label={t("app_retirement.business_justification")}
                                                        placeholder={t("app_retirement.type_here_placeholder")}
                                                        rows={3}
                                                        style={{ width: "100%"}}
                                                        onChange={(e) => setBusinessJustification(e.target.value)}
                                                        value={businessJustification}
                                                        showToolbar={false}
                                                        required={true}
                                                        showCharacterCount={true}
                                                        maxLength={100}
                                                        showLabelIcon={false}
                                                        disableResize={true}
                                                        className="app-retirement-business-justification w-100"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ ...(!isMobile && { flex: "1" }) }}>
                                    <div className="card card-content align-items-center m-0 mb-3">
                                        <div className="ad-table-header help-section align-items-center d-flex mb-3" style={{ gap: "8px" }}>
                                            <ButtonIcon
                                                className=""
                                                color="Interaction/Active"
                                                icon="check_circle"
                                                label="Label"
                                                onClick={function wRe() { }}
                                                size="medium"
                                                type="default"
                                                style={{ cursor: "default" }}
                                            />
                                            <div className="help-page-text">{t("app_retirement.approval_process")}</div>
                                        </div>
                                        <div className="non-disclosure-text">{t("app_retirement.approval_process_desc")}</div>
                                    </div>
                                    <div className="card card-content align-items-center m-0">
                                        <div className="ad-table-header help-section source-icon align-items-center d-flex mb-3" style={{ gap: "8px" }}>
                                            <ButtonIcon
                                                className=""
                                                color="Interaction/Active"
                                                icon="warning_amber"
                                                label="Label"
                                                onClick={function wRe() { }}
                                                size="medium"
                                                type="default"
                                                style={{ cursor: "default" }}
                                            />
                                            <div className="help-page-text">{t("app_retirement.what_submitting_this_request_will_do")}</div>
                                        </div>
                                        <div className="non-disclosure-text d-flex" style={{ gap: ".25rem" }}>
                                            <Icon
                                                alt="alt value"
                                                icon="check"
                                                size="18px"
                                                style={{ fontSize: "20px" }}
                                            />
                                            <div><strong>{t("app_retirement.myidm_application")}</strong>{` ${t("app_retirement.what_submitting_this_request_will_do_desc")}`}</div>
                                        </div>
                                        <hr style={{ height: "1px", width: "94%", color: "#E5E5E5" }} />
                                        <div className="ad-table-header help-section">
                                            <div className="help-page-text">{t("app_retirement.connected_endpoints_will_be_removed")}</div>
                                        </div>
                                        <div className="non-disclosure-text d-flex" style={{ gap: ".25rem" }}>
                                            <Icon
                                                alt="alt value"
                                                icon="check"
                                                size="18px"
                                                style={{ fontSize: "20px", color: "#D6AC16" }}
                                            />
                                            <div><strong>{t("app_retirement.idx_Identity_Exchange")}</strong>{` ${t("app_retirement.idx_Identity_Exchange_text")}`}</div>
                                        </div>
                                        <div className="non-disclosure-text d-flex" style={{ gap: ".25rem" }}>
                                            <Icon
                                                alt="alt value"
                                                icon="check"
                                                size="18px"
                                                style={{ fontSize: "20px", color: "#D6AC16" }}
                                            />
                                            <div><strong>{t("app_retirement.active_directory")}</strong>{` ${t("app_retirement.active_directory_text")}`}</div>
                                        </div>
                                        <div className="non-disclosure-text d-flex" style={{ gap: ".25rem" }}>
                                            <Icon
                                                alt="alt value"
                                                icon="check"
                                                size="18px"
                                                style={{ fontSize: "20px", color: "#D6AC16" }}
                                            />
                                            <div><strong>{t("app_retirement.web_service_api_gateway")}</strong>{` ${t("app_retirement.web_service_api_gateway_text")}`}</div>
                                        </div>
                                        <div className="certificates-revoked-section">
                                            <Icon
                                                alt="alt value"
                                                icon="warning_amber"
                                                size="18px"
                                                style={{ fontSize: "20px", color: "#D6AC16" }}
                                            />
                                            <div className="ad-table-header help-section">
                                                <div className="help-page-text">{t("app_retirement.all_associated_certificates_will_be_revoked")}</div>
                                                <div className="non-disclosure-text">{t("app_retirement.all_associated_certificates_will_be_revoked_text")}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="entra-guest-footer mt-3">
                <div className="row footer-row-with-footer-buttons">
                    <Checkbox
                        className=""
                        feedbackType="info"
                        helperText=""
                        size="small"
                        style={{}}
                        checked ={isConfirmed}
                        onUpdate={(event) => {
                            setIsConfirmed(event);
                        }}
                        text={t("app_retirement.acknowledge_text")}
                    />
                    <div className="footer-buttons">
                        <div className="col-md-auto align-self-center d-flex mb-3 mb-md-0 px-md-0 justify-content-center">
                            <Button
                                className="search-user-cancel-button mr-md-2 my-0"
                                type="button"
                                size="medium"
                                text={t("RequestGPIDForNonEmployee.cancel_btn_label")}
                                variant="tertiary"
                                onClick={() => navigate("/home")}
                            />
                        </div>
                        <div className="col-md-auto align-self-center d-flex mb-3 mb-md-0">
                            <Button
                                className="search-user-select-button request-button my-0 px-3 justify-content-center"
                                type="button"
                                size="medium"
                                text={t("nonArExtension.submit")}
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={disableSubmit()}
                            />
                        </div>
                    </div>
                </div>
                <ToastContainer
                    position="bottom-center"
                />
                {showModal && renderConfirmModal()}
            </div>
        </>
    )
}

export default AppRetirement